import React, { useState, useEffect, useRef } from 'react';

/**
 * 车务服务页面 - 重构版本
 * 包含交互式产品工具展示组件
 */
const RentalServicePage: React.FC = () => {
  // 一级选项状态管理
  const [activeTab, setActiveTab] = useState<'saas' | 'miniapp' | 'mall'>('saas');
  // 二级选项状态管理（仅在车务SAAS模式下使用）
  const [activeSubTab, setActiveSubTab] = useState<string>('租金回款');
  // 自动播放状态管理
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 车务SAAS的二级选项和对应图片
  const saasOptions = [
    { name: '租金回款', image: '/A1资金回款.png' },
    { name: '资产管理', image: '/A2资产管理.png' },
    { name: '违章转移', image: '/A3违章转移.png' },
    { name: '保险控费', image: '/A4保险控费.png' },
    { name: '事故管理', image: '/A5事故管理.png' },
    { name: '电子签约', image: '/A6电子签约.png' }
  ];

  /**
   * 自动播放逻辑 - 每2秒切换到下一个选项
   */
  useEffect(() => {
    // 只在车务SAAS模式下且自动播放开启时启动定时器
    if (activeTab === 'saas' && isAutoPlaying) {
      autoPlayTimerRef.current = setInterval(() => {
        setActiveSubTab(prevTab => {
          const currentIndex = saasOptions.findIndex(option => option.name === prevTab);
          const nextIndex = (currentIndex + 1) % saasOptions.length;
          return saasOptions[nextIndex].name;
        });
      }, 2000); // 每2秒切换
    }

    // 清理定时器
    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
        autoPlayTimerRef.current = null;
      }
    };
  }, [activeTab, isAutoPlaying, saasOptions]);

  /**
   * 组件卸载时清理定时器
   */
  useEffect(() => {
    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, []);

  /**
   * 处理二级选项按钮点击 - 停止自动播放
   */
  const handleSubTabClick = (optionName: string) => {
    setActiveSubTab(optionName);
    setIsAutoPlaying(false); // 用户点击时停止自动播放
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  };

  /**
   * 获取当前显示的图片
   */
  const getCurrentImage = () => {
    if (activeTab === 'saas') {
      const option = saasOptions.find(opt => opt.name === activeSubTab);
      return option?.image || '/A1资金回款.png';
    } else if (activeTab === 'miniapp') {
      return '/3.移动车务小程序.png';
    } else {
      return '/4.线上门店数字化.png';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero区 - 车务hero区图片 */}
      <div className="w-full">
        <img 
          src="/1.车务hero区.png" 
          alt="车务服务Hero区"
          className="w-full h-auto object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='600' viewBox='0 0 1200 600'%3E%3Crect width='1200' height='600' fill='%23f3f4f6'/%3E%3Ctext x='600' y='300' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='24' fill='%236b7280'%3E车务服务Hero区%3C/text%3E%3C/svg%3E`;
          }}
        />
      </div>

      {/* 全场景应用产品工具组件 */}
      <div className="w-full py-16 bg-gray-50">
        <div className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* 标题区域 */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                全场景应用 产品工具
              </h2>
              <p className="text-xl text-gray-600">
                上手快、易操作、成本低、体验佳
              </p>
            </div>

            {/* 一级选项按钮 */}
            <div className="flex justify-center mb-8">
              <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setActiveTab('saas')}
                  className={`px-8 py-4 text-lg font-medium transition-all duration-300 ${
                    activeTab === 'saas'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  车务SAAS
                </button>
                <button
                  onClick={() => setActiveTab('miniapp')}
                  className={`px-8 py-4 text-lg font-medium transition-all duration-300 border-l border-r border-gray-200 ${
                    activeTab === 'miniapp'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  微信小程序助手
                </button>
                <button
                  onClick={() => setActiveTab('mall')}
                  className={`px-8 py-4 text-lg font-medium transition-all duration-300 ${
                    activeTab === 'mall'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  车辆租赁商城
                </button>
              </div>
            </div>
          </div>
          
          {/* 内容展示区域 */}
          <div className="w-full bg-white overflow-hidden">
            {/* 车务SAAS模式 - 显示二级按钮和轮播图 */}
            {activeTab === 'saas' && (
              <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  {/* 二级选项按钮 */}
                  <div className="flex flex-wrap justify-center gap-4 mb-8">
                    {saasOptions.map((option) => (
                      <button
                        key={option.name}
                        onClick={() => handleSubTabClick(option.name)}
                        className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                          activeSubTab === option.name
                            ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                        }`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                  
                  {/* 图片展示区域 */}
                  <div className="flex justify-center">
                    <div className="w-full" style={{ maxWidth: 'calc(1024px * 1.5)', height: 'calc(500px * 1.5)' }}>
                      <img
                        src={getCurrentImage()}
                        alt={activeSubTab}
                        className="w-full h-full object-contain rounded-lg shadow-md transition-all duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='750' viewBox='0 0 1200 750'%3E%3Crect width='1200' height='750' fill='%23f3f4f6'/%3E%3Ctext x='600' y='375' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3E${activeSubTab}%3C/text%3E%3C/svg%3E`;
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 微信小程序助手模式 */}
            {activeTab === 'miniapp' && (
              <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
                  <div className="max-w-4xl w-full">
                    <img
                      src="/3.移动车务小程序.png"
                      alt="移动车务小程序助手"
                      className="w-full h-auto object-contain rounded-lg shadow-md"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500' viewBox='0 0 800 500'%3E%3Crect width='800' height='500' fill='%23f3f4f6'/%3E%3Ctext x='400' y='250' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3E移动车务小程序助手%3C/text%3E%3C/svg%3E`;
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 车辆租赁商城模式 */}
            {activeTab === 'mall' && (
              <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
                  <div className="max-w-4xl w-full">
                    <img
                      src="/4.线上门店数字化.png"
                      alt="线上门店数字化运营"
                      className="w-full h-auto object-contain rounded-lg shadow-md"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500' viewBox='0 0 800 500'%3E%3Crect width='800' height='500' fill='%23f3f4f6'/%3E%3Ctext x='400' y='250' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3E线上门店数字化运营%3C/text%3E%3C/svg%3E`;
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部区域 - 车务body底图片 */}
      <div className="w-full">
        <img 
          src="/2.车务body底.png" 
          alt="车务服务底部区域"
          className="w-full h-auto object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='400' viewBox='0 0 1200 400'%3E%3Crect width='1200' height='400' fill='%23f8fafc'/%3E%3Ctext x='600' y='200' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='24' fill='%236b7280'%3E车务服务底部区域%3C/text%3E%3C/svg%3E`;
          }}
        />
      </div>
    </div>
  );
};

export default RentalServicePage;